import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import CloudLoader from '../CloudLoader';
import LogoutModal from '../LogoutModal';

import config from '../config';
const API = config.API_BASE;

function StudentDashboard() {
  const [profile, setProfile] = useState(null);
  const [exams, setExams] = useState([]);
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(true);
  const [showAddExam, setShowAddExam] = useState(false);
  const [newExam, setNewExam] = useState({ subject:'', score:'', totalMarks:'', examDate:'' });
  const [message, setMessage] = useState('');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const navigate = useNavigate();
  const userId = sessionStorage.getItem('userId');

  useEffect(() => {
  fetchProfile();
  fetchExams();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/students/${userId}`);
      setProfile(res.data);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  const fetchExams = async () => {
    try {
      const res = await axios.get(`${API}/students/${userId}/exams`);
      setExams(res.data);
    } catch(e) { console.error(e); }
  };

  const handleAddExam = async () => {
    try {
      await axios.post(`${API}/students/exams`, {
        studentId: userId,
        name: profile?.name,
        subject: newExam.subject,
        score: parseInt(newExam.score),
        totalMarks: parseInt(newExam.totalMarks),
        examDate: newExam.examDate
      });
      setMessage('Exam record added!');
      setShowAddExam(false);
      setNewExam({ subject:'', score:'', totalMarks:'', examDate:'' });
      fetchExams();
    } catch(e) { setMessage('Error adding exam'); }
  };

  const handleLogout = () => {
    sessionStorage.clear();
    navigate('/');
  };

  const getPercentage = (score, total) => {
    return total > 0 ? ((score/total)*100).toFixed(1) : 0;
  };

  const getGrade = (percentage) => {
    if (percentage >= 90) return { grade: 'A+', color: 'text-green-600' };
    if (percentage >= 80) return { grade: 'A', color: 'text-green-500' };
    if (percentage >= 70) return { grade: 'B', color: 'text-blue-500' };
    if (percentage >= 60) return { grade: 'C', color: 'text-yellow-500' };
    return { grade: 'F', color: 'text-red-500' };
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center">
    <CloudLoader message="Loading your profile..." />
  </div>
  );

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Navbar */}
      <nav className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-3">
          <img src="/cloudrecords-logo.svg" alt="CloudRecords" className="w-8 h-8" />
          <div>
            <h1 className="font-bold text-lg leading-tight">CloudRecords</h1>
            <p className="text-xs text-slate-400">Student Dashboard</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-slate-300">👤 {userId}</span>
          <button onClick={() => setShowLogoutModal(true)} className="bg-red-600 hover:bg-red-700 text-white text-sm px-4 py-2 rounded-lg transition">
  Logout
</button>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-8">

        {/* Welcome Card */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-6 mb-8 text-white shadow-lg">
          <p className="text-blue-200 text-sm mb-1">Welcome back,</p>
          <h2 className="text-2xl font-bold">{profile?.name || userId}</h2>
          <div className="flex gap-6 mt-3">
            <span className="text-blue-200 text-sm">🎓 {profile?.department || '-'}</span>
            <span className="text-blue-200 text-sm">📅 Year {profile?.year || '-'}</span>
            <span className="text-blue-200 text-sm">🆔 {userId}</span>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
            <p className="text-slate-500 text-sm">Exams Taken</p>
            <p className="text-4xl font-bold text-blue-600 mt-1">{exams.length}</p>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
            <p className="text-slate-500 text-sm">Average Score</p>
            <p className="text-4xl font-bold text-green-600 mt-1">
              {exams.length > 0
                ? (exams.reduce((acc, e) => acc + getPercentage(e.score, e.totalMarks) * 1, 0) / exams.length).toFixed(1) + '%'
                : 'N/A'}
            </p>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
            <p className="text-slate-500 text-sm">Department</p>
            <p className="text-4xl font-bold text-purple-600 mt-1">{profile?.department || '-'}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {['profile', 'exams'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-5 py-2.5 rounded-xl text-sm font-medium capitalize transition ${
                activeTab === tab
                  ? 'bg-blue-600 text-white shadow'
                  : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
              }`}
            >
              {tab === 'profile' ? '👤 My Profile' : '📝 Exam Records'}
            </button>
          ))}
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <h2 className="font-semibold text-slate-800 mb-6">My Profile</h2>
            {profile ? (
              <div className="grid grid-cols-2 gap-6">
                {[
                  { label: 'Full Name', value: profile.name },
                  { label: 'Student ID', value: profile.studentId },
                  { label: 'Department', value: profile.department },
                  { label: 'Year', value: `Year ${profile.year}` },
                  { label: 'Email', value: profile.email },
                  { label: 'Enrolled On', value: profile.createdAt ? profile.createdAt.split('T')[0] : '-' },
                ].map((item, i) => (
                  <div key={i} className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs text-slate-400 mb-1">{item.label}</p>
                    <p className="font-medium text-slate-800">{item.value || '-'}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400">Profile not found. Please contact admin.</p>
            )}
          </div>
        )}

        {/* Exams Tab */}
        {activeTab === 'exams' && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
              <h2 className="font-semibold text-slate-800">Exam Records</h2>
              <button
                onClick={() => setShowAddExam(!showAddExam)}
                className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded-lg transition"
              >
                + Add Exam
              </button>
            </div>

            {message && (
              <div className="mx-6 mt-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm">
                {message}
              </div>
            )}

            {showAddExam && (
              <div className="px-6 py-4 bg-blue-50 border-b border-blue-100">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { key: 'subject', placeholder: 'Subject Name' },
                    { key: 'examDate', placeholder: 'Exam Date (e.g. 2024-05)' },
                    { key: 'score', placeholder: 'Score (e.g. 85)' },
                    { key: 'totalMarks', placeholder: 'Total Marks (e.g. 100)' },
                  ].map(field => (
                    <input
                      key={field.key}
                      placeholder={field.placeholder}
                      value={newExam[field.key]}
                      onChange={(e) => setNewExam({...newExam, [field.key]: e.target.value})}
                      className="px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                    />
                  ))}
                </div>
                <button
                  onClick={handleAddExam}
                  className="mt-4 bg-blue-600 hover:bg-blue-700 text-white text-sm px-6 py-2.5 rounded-lg transition"
                >
                  Save Exam Record
                </button>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                  <tr>
                    <th className="px-6 py-3 text-left">Subject</th>
                    <th className="px-6 py-3 text-left">Score</th>
                    <th className="px-6 py-3 text-left">Total</th>
                    <th className="px-6 py-3 text-left">Percentage</th>
                    <th className="px-6 py-3 text-left">Grade</th>
                    <th className="px-6 py-3 text-left">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {exams.length === 0 ? (
                    <tr><td colSpan="6" className="px-6 py-8 text-center text-slate-400">No exam records found</td></tr>
                  ) : (
                    exams.map((e, i) => {
                      const pct = getPercentage(e.score, e.totalMarks);
                      const { grade, color } = getGrade(pct);
                      return (
                        <tr key={i} className="hover:bg-slate-50">
                          <td className="px-6 py-4 font-medium">{e.subject}</td>
                          <td className="px-6 py-4">{e.score}</td>
                          <td className="px-6 py-4">{e.totalMarks}</td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <div className="w-24 bg-slate-200 rounded-full h-2">
                                <div className="bg-blue-600 h-2 rounded-full" style={{width: `${pct}%`}}></div>
                              </div>
                              <span>{pct}%</span>
                            </div>
                          </td>
                          <td className={`px-6 py-4 font-bold ${color}`}>{grade}</td>
                          <td className="px-6 py-4 text-slate-400">{e.recordType?.replace('EXAM#', '')}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
{showLogoutModal && (
  <LogoutModal
    onConfirm={() => { setShowLogoutModal(false); handleLogout(); }}
    onCancel={() => setShowLogoutModal(false)}
  />
)}

    </div>
  );
}

export default StudentDashboard;